import { type FetchResult, type MutationFunctionOptions } from "@apollo/client";
import {
  type CreateAchPaymentPrefInput,
  type CreateCheckPaymentPrefInput,
  type CreateWirePaymentPrefInput,
  type SubmitChangeRequestPaymentPrefMutation,
  type SubmitChangeRequestPaymentPrefMutationVariables,
  type UpdatePaymentPrefForPositionsInAccountMutationVariables,
  type UpdatePaymentPrefForPositionsOfPaymentPrefMutationVariables,
  type UpdatePaymentPrefForSinglePositionMutationVariables,
  type UpdatePaymentPrefsForPositionsInAccountWithoutPaymentPrefsMutationVariables,
} from "../../../../graphql/supergraph.index";
import {
  AssociatePaymentPrefType,
  BankAccountType,
} from "../../../../graphql/supergraphOperations";
import {
  type DetailedPaymentPrefUnionObjectType,
  type DetailedWirePaymentPrefObjectType,
} from "../../common/types";
import { AssociatePaymentPrefMutationTypeEnum } from "../../Context/enums";
import {
  ERROR_UPDATING_PREF_MISSING_INFORMATION,
  CustomConfirmationError,
  ERROR_UPDATING_PREF_INVALID_TYPE,
} from "./errors";

export const hasIntermediaryBank = (
  paymentPrefDetail: DetailedWirePaymentPrefObjectType,
): boolean => {
  const intermediaryBank = paymentPrefDetail?.intermediaryBank;
  if (!intermediaryBank) {
    return false;
  }

  return (
    !!intermediaryBank.intlRoutingCode ||
    !!intermediaryBank.name ||
    !!intermediaryBank.abaNumber ||
    !!intermediaryBank.swiftCode ||
    !!intermediaryBank.address?.city ||
    !!intermediaryBank.address?.country?.id ||
    !!intermediaryBank.address?.postalCode ||
    !!intermediaryBank.address?.region ||
    !!intermediaryBank.address?.state?.id ||
    !!intermediaryBank.address?.street ||
    !!intermediaryBank.address?.street2 ||
    !!intermediaryBank.address?.street3
  );
};

export const convertPaymentPrefDetailToInputType = (
  paymentPrefDetail: DetailedPaymentPrefUnionObjectType,
  arenaId: string | undefined,
  signature?: string,
): CreateAchPaymentPrefInput | CreateWirePaymentPrefInput | CreateCheckPaymentPrefInput => {
  if (paymentPrefDetail.__typename === "AchPaymentPrefObjectType") {
    return {
      accountName: paymentPrefDetail.accountName ?? "",
      accountNumber: paymentPrefDetail.accountNumber ?? "",
      accountOwnership: paymentPrefDetail.accountOwnership,
      accountType: paymentPrefDetail.accountType ?? BankAccountType.Checking,
      additionalInstructions: paymentPrefDetail.additionalInstructions,
      arenaId: arenaId ?? "",
      bankName: paymentPrefDetail.bankName ?? "",
      routingNumber: paymentPrefDetail.routingNumber ?? "",
      signatureFullName: signature ?? paymentPrefDetail.signatureFullName,
    };
  }

  if (paymentPrefDetail.__typename === "CheckPaymentPrefObjectType") {
    return {
      arenaId: arenaId ?? "",
      memo: paymentPrefDetail.memo,
      payeeName: paymentPrefDetail.payeeName ?? "",
      useAccountMailingAddress: paymentPrefDetail.useAccountMailingAddress || false,
      address: {
        city: paymentPrefDetail?.address?.city ?? "",
        countryId: paymentPrefDetail?.address?.country?.id ?? "",
        postalCode: paymentPrefDetail?.address?.postalCode ?? "",
        region: paymentPrefDetail?.address?.region ?? null,
        stateId: paymentPrefDetail?.address?.state?.id ?? null,
        street: paymentPrefDetail?.address?.street ?? "",
        street2: paymentPrefDetail?.address?.street2,
        street3: paymentPrefDetail?.address?.street3,
      },
    };
  }

  if (paymentPrefDetail.__typename === "WirePaymentPrefObjectType") {
    return {
      arenaId: arenaId ?? "",
      bank: {
        intlRoutingNumber: paymentPrefDetail.bank?.intlRoutingCode,
        name: paymentPrefDetail.bank?.name ?? "",
        routingNumber: paymentPrefDetail.bank?.abaNumber,
        swiftCode: paymentPrefDetail.bank?.swiftCode,
        address: {
          city: paymentPrefDetail?.bank?.address?.city,
          countryId: paymentPrefDetail?.bank?.address?.country?.id ?? "",
          postalCode: paymentPrefDetail?.bank?.address?.postalCode,
          region: paymentPrefDetail?.bank?.address?.region ?? null,
          stateId: paymentPrefDetail?.bank?.address?.state?.id ?? null,
          street: paymentPrefDetail?.bank?.address?.street,
          street2: paymentPrefDetail?.bank?.address?.street2,
          street3: paymentPrefDetail?.bank?.address?.street3,
        },
      },
      beneficiaryAddress: {
        city: paymentPrefDetail?.beneficiaryAddress?.city ?? "",
        countryId: paymentPrefDetail?.beneficiaryAddress?.country?.id ?? "",
        postalCode: paymentPrefDetail?.beneficiaryAddress?.postalCode ?? "",
        region: paymentPrefDetail?.beneficiaryAddress?.region ?? null,
        stateId: paymentPrefDetail?.beneficiaryAddress?.state?.id ?? null,
        street: paymentPrefDetail?.beneficiaryAddress?.street ?? "",
        street2: paymentPrefDetail?.beneficiaryAddress?.street2,
        street3: paymentPrefDetail?.beneficiaryAddress?.street3,
      },
      beneficiaryAccountNumber: paymentPrefDetail.beneficiaryAccountNumber ?? "",
      beneficiaryName: paymentPrefDetail.beneficiaryName ?? "",
      beneficiaryType: paymentPrefDetail.beneficiaryType,
      hasIntermediary: !!paymentPrefDetail.intermediaryBank,
      referenceToBeneficiary: paymentPrefDetail.referenceToBeneficiary,
      wireObi: paymentPrefDetail.wireObi
        ? {
            field0: paymentPrefDetail.wireObi?.field0,
            field1: paymentPrefDetail.wireObi?.field1,
            field2: paymentPrefDetail.wireObi?.field2,
            field3: paymentPrefDetail.wireObi?.field3,
          }
        : paymentPrefDetail.wireObi,
      wireBbi: paymentPrefDetail.wireBbi
        ? {
            field0: paymentPrefDetail.wireBbi?.field0,
            field1: paymentPrefDetail.wireBbi?.field1,
            field2: paymentPrefDetail.wireBbi?.field2,
            field3: paymentPrefDetail.wireBbi?.field3,
          }
        : paymentPrefDetail.wireBbi,
      intermediaryBank: hasIntermediaryBank(paymentPrefDetail)
        ? {
            intlRoutingNumber: paymentPrefDetail?.intermediaryBank?.intlRoutingCode,
            name: paymentPrefDetail?.intermediaryBank?.name ?? "",
            routingNumber: paymentPrefDetail?.intermediaryBank?.abaNumber,
            swiftCode: paymentPrefDetail?.intermediaryBank?.swiftCode,
            address: {
              city: paymentPrefDetail?.intermediaryBank?.address?.city,
              countryId: paymentPrefDetail?.intermediaryBank?.address?.country?.id ?? "",
              postalCode: paymentPrefDetail?.intermediaryBank?.address?.postalCode,
              region: paymentPrefDetail?.intermediaryBank?.address?.region ?? null,
              stateId: paymentPrefDetail?.intermediaryBank?.address?.state?.id ?? null,
              street: paymentPrefDetail?.intermediaryBank?.address?.street,
              street2: paymentPrefDetail?.intermediaryBank?.address?.street2,
              street3: paymentPrefDetail?.intermediaryBank?.address?.street3,
            },
          }
        : null,
    };
  }

  return {} as CreateAchPaymentPrefInput;
};

export type AssociateMutationVariablesType =
  | UpdatePaymentPrefForPositionsOfPaymentPrefMutationVariables
  | UpdatePaymentPrefForPositionsInAccountMutationVariables
  | UpdatePaymentPrefsForPositionsInAccountWithoutPaymentPrefsMutationVariables
  | UpdatePaymentPrefForSinglePositionMutationVariables;

export const getAssociationMutationVariables = (
  associateMutationType: AssociatePaymentPrefMutationTypeEnum,
  accountId?: string,
  sourcePaymentPrefId?: string,
  targetPaymentPrefId?: string,
  positionId?: string,
  paymentPrefId?: string,
): AssociateMutationVariablesType | undefined => {
  if (
    associateMutationType ===
    AssociatePaymentPrefMutationTypeEnum.AllOtherPositionsWithPaymentPref
  ) {
    if (!sourcePaymentPrefId || !targetPaymentPrefId) {
      return undefined;
    }
    return {
      objectInput: {
        sourcePaymentPrefId,
        targetPaymentPrefId,
      },
    };
  }

  if (
    associateMutationType === AssociatePaymentPrefMutationTypeEnum.AllOtherPositionsInAccount
  ) {
    if (!paymentPrefId || !accountId) {
      return undefined;
    }
    return {
      objectInput: {
        paymentPrefId,
        accountIds: [accountId],
      },
    };
  }

  if (
    associateMutationType ===
    AssociatePaymentPrefMutationTypeEnum.PositionsWithoutPaymentPrefOfAccount
  ) {
    if (!paymentPrefId || !accountId) {
      return undefined;
    }
    return {
      objectInput: {
        paymentPrefId,
        accountId,
      },
    };
  }

  if (associateMutationType === AssociatePaymentPrefMutationTypeEnum.OnlyThisPosition) {
    if (!paymentPrefId || !positionId) {
      return undefined;
    }
    return {
      objectInput: {
        paymentPrefId,
        positionId,
      },
    };
  }

  // Even though all enum's are taken care of TS requires a default value to be returned.
  // We return undefined in the function to indicate if there were missing parameters that should have been passed in
  // to create the correct Variables object or if an item passed in was casted incorrectly
  return undefined;
};

export type ChangeRequestPayloadsInput = {
  associatePaymentPref: AssociatePaymentPrefMutationTypeEnum;
  arenaId: string;
  targetPaymentPrefId: string;
  accountId: string;
  sourcePaymentPrefId?: string;
  positionId?: string;
  bypassCheckForAcceptedPaymentMethods?: boolean;
  accountName?: string;
};

export const getChangeRequestPayloads = ({
  associatePaymentPref,
  arenaId,
  targetPaymentPrefId,
  accountId,
  sourcePaymentPrefId,
  positionId,
  bypassCheckForAcceptedPaymentMethods = false,
  accountName,
}: ChangeRequestPayloadsInput) => {
  if (
    associatePaymentPref ===
    AssociatePaymentPrefMutationTypeEnum.AllOtherPositionsWithPaymentPref
  ) {
    if (!sourcePaymentPrefId) {
      return undefined;
    }
    return {
      arenaId,
      targetPaymentPrefId,
      accountId,
      sourcePaymentPrefId,
      associatePaymentPrefType: AssociatePaymentPrefType.AllOtherPositionsWithPaymentPref,
      accountName,
    };
  } else if (associatePaymentPref === AssociatePaymentPrefMutationTypeEnum.OnlyThisPosition) {
    if (!positionId) {
      return undefined;
    }
    return {
      arenaId,
      targetPaymentPrefId,
      accountId,
      positionId,
      associatePaymentPrefType: AssociatePaymentPrefType.OnlyThisPosition,
      accountName,
    };
  } else if (
    associatePaymentPref === AssociatePaymentPrefMutationTypeEnum.AllOtherPositionsInAccount
  ) {
    return {
      arenaId,
      targetPaymentPrefId,
      accountIds: [accountId],
      associatePaymentPrefType: AssociatePaymentPrefType.AllOtherPositionsInAccount,
      bypassCheckForAcceptedPaymentMethods,
      accountName,
    };
  } else if (
    associatePaymentPref ===
    AssociatePaymentPrefMutationTypeEnum.PositionsWithoutPaymentPrefOfAccount
  ) {
    return {
      arenaId,
      targetPaymentPrefId,
      accountId,
      associatePaymentPrefType: AssociatePaymentPrefType.PositionsWithoutPaymentPrefOfAccount,
      accountName,
    };
  }

  // Edge case where enum passed in is not taken care of. Should not hit this error.
  throw new CustomConfirmationError(ERROR_UPDATING_PREF_INVALID_TYPE);
};

export const submitChangeRequest = async (
  createChangeRequest: (
    options?: MutationFunctionOptions<
      SubmitChangeRequestPaymentPrefMutation,
      SubmitChangeRequestPaymentPrefMutationVariables
    >,
  ) => Promise<FetchResult<SubmitChangeRequestPaymentPrefMutation>>,
  associatePaymentPref: AssociatePaymentPrefMutationTypeEnum,
  arenaId: string,
  targetPaymentPrefId: string,
  accountId: string,
  sourcePaymentPrefId?: string,
  positionId?: string,
  bypassCheckForAcceptedPaymentMethods = false,
  accountName?: string,
) => {
  const changeRequestPayload = getChangeRequestPayloads({
    associatePaymentPref,
    arenaId,
    targetPaymentPrefId,
    accountId,
    sourcePaymentPrefId,
    positionId,
    bypassCheckForAcceptedPaymentMethods,
    accountName,
  });

  if (!changeRequestPayload) {
    throw new CustomConfirmationError(ERROR_UPDATING_PREF_MISSING_INFORMATION);
  }

  await createChangeRequest({
    variables: {
      payloads: [
        {
          requestPayload: changeRequestPayload,
        },
      ],
    },
  });
};
